import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Button, Stack,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { SECTION_COLORS } from "../../constants";

const ORIGIN_STYLE = {
    custom: { bgcolor: "#f0f9ff", color: "#0369a1" },
    system: { bgcolor: "#FFF8E1", color: "#b45309" },
    external: { bgcolor: "#fdf4ff", color: "#7e22ce" },
};

const ViewEventTypeDialog = ({ open, eventType, onClose, onEditRequest }) => {
    if (!eventType) return null;

    const originStyle = ORIGIN_STYLE[eventType.origin] || { bgcolor: "#f5f5f5", color: "#555" };
    const hasSchema = eventType.payload_schema && Object.keys(eventType.payload_schema).length > 0;

    const rows = [
        { label: "Clé (key)", value: eventType.key },
        { label: "Marque", value: eventType.brand || "Globale" },
        { label: "Identifiant", value: eventType.id },
        { label: "Créé le", value: eventType.created_at ? new Date(eventType.created_at).toLocaleDateString("fr-FR") : "–" },
        { label: "Mis à jour le", value: eventType.updated_at ? new Date(eventType.updated_at).toLocaleDateString("fr-FR") : "–" },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.eventTypes.gradient }}>
                        <EventNoteIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            {eventType.name}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">Détail du type d'événement</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Status + origin banner */}
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={2} py={1.25}
                    sx={{
                        borderRadius: 2,
                        bgcolor: eventType.active ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${eventType.active ? "#86efac" : "#fca5a5"}`
                    }}>
                    {eventType.active
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                        : <CancelIcon sx={{ fontSize: 18, color: "#DC2626" }} />}
                    <Typography fontSize={13} fontWeight={600} color={eventType.active ? "#16a34a" : "#DC2626"}>
                        {eventType.active ? "Actif" : "Inactif"}
                    </Typography>
                    <Chip label={eventType.origin} size="small"
                        sx={{ ml: "auto", fontWeight: 700, fontSize: 11, ...originStyle }} />
                </Box>

                {/* Description */}
                {eventType.description && (
                    <Typography fontSize={13} color="text.secondary" mb={2}
                        sx={{ fontStyle: "italic", borderLeft: `3px solid ${SECTION_COLORS.eventTypes.main}50`, pl: 1.5 }}>
                        {eventType.description}
                    </Typography>
                )}

                {/* Rows */}
                <Stack spacing={1}>
                    {rows.map(({ label, value }) => (
                        <Box key={label} display="flex" alignItems="flex-start" justifyContent="space-between"
                            px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={12} color="text.secondary" fontWeight={500} sx={{ minWidth: 140 }}>{label}</Typography>
                            <Typography fontSize={12} fontWeight={600} color="#1a1a2e" sx={{ textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
                        </Box>
                    ))}
                </Stack>

                {/* payload_schema */}
                {hasSchema && (
                    <Box mt={2} p={1.5} sx={{ borderRadius: 2, bgcolor: "#f0f9ff", border: "1px solid #bae6fd" }}>
                        <Typography fontSize={11} fontWeight={700} color={SECTION_COLORS.eventTypes.main} mb={0.5}>
                            payload_schema
                        </Typography>
                        <Typography component="pre" fontSize={11}
                            sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#1a1a2e", m: 0 }}>
                            {JSON.stringify(eventType.payload_schema, null, 2)}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                <Button variant="contained" onClick={() => { onClose(); onEditRequest(eventType); }}
                    startIcon={<EditIcon />}
                    sx={{ background: SECTION_COLORS.eventTypes.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    Modifier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewEventTypeDialog;
